// Thin typed fetch wrapper, per contracts/api.md. `/api` is proxied to the backend Worker by
// Vite in dev (vite.config.ts) and by a Cloudflare Pages Function in production
// (functions/api/[[path]].ts) — both keep frontend and API same-origin, so this file never
// needs to know the backend's real URL, and the session cookie stays a simple `SameSite=Lax`.

const API_BASE = "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API error ${status}`);
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // FormData (image uploads) must NOT get a manually-set Content-Type — the browser needs to
  // add its own multipart boundary, which only happens if we leave the header alone.
  const isFormData = init?.body instanceof FormData;
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body && !isFormData ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // no JSON body — leave `body` null
    }
    throw new ApiError(response.status, body);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
}

function patch<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "PATCH", body: body === undefined ? undefined : JSON.stringify(body) });
}

function get<T>(path: string): Promise<T> {
  return apiFetch<T>(path);
}

function del<T>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: "DELETE" });
}

// --- Auth (contracts/api.md > Auth) ---

export type LockedOutError = { error: "locked_out"; retryAfterSeconds: number };

// FR-087: `rememberMe` is a plain intent flag — the server decides the actual session length
// (30 days when set, otherwise 8h/12h) and bakes it into the signed token.
export function loginCoMember(email: string, password: string, rememberMe = false) {
  return post<{ role: "co_member" }>("/auth/co-member/login", { email, password, rememberMe });
}

export function loginMember(slug: string, email: string, password: string, rememberMe = false) {
  return post<{ role: "member"; isManager: boolean; takesTest: boolean }>(
    `/company/${encodeURIComponent(slug)}/login`,
    { email, password, rememberMe },
  );
}

export function logout() {
  return post<void>("/auth/logout");
}

export function getCompanyBranding(slug: string) {
  return get<{ name: string; logoUrl: string | null }>(`/company/${encodeURIComponent(slug)}/branding`);
}

export function getCoMemberMe() {
  return get<{ fullName: string; email: string }>("/co/co-members/me");
}

// FR-044: self-service password change for the signed-in CO Member's own account.
export function changeCoMemberPassword(currentPassword: string, newPassword: string) {
  return patch<{ fullName: string; email: string }>("/co/co-members/me", { currentPassword, newPassword });
}

export function createCoMember(fullName: string, email: string, password: string) {
  return post<{ id: string; fullName: string; email: string }>("/co/co-members", {
    fullName,
    email,
    password,
  });
}

export function getMe() {
  return get<{
    fullName: string;
    email: string;
    department: string | null;
    isManager: boolean;
    takesTest: boolean;
  }>("/me");
}

// Domain-specific endpoint functions live in their own files (e.g. questions-api.ts,
// uploads-api.ts) and import these three (plus apiFetch, for non-JSON bodies like uploads).
export { get, post, patch, del, apiFetch };
